import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import {
  FormControl,
  Grid,
  TextField,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Input,
  FormHelperText,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import {
  getSpecialOffers,
  getPointOfSales,
  addSpecialOffer,
  deleteSpecialOffer,
  updateSpecialOffer
} from 'actions/productActions';
import { arrayToKeys } from 'utils/parsingUtils';
import Skeleton from '@mui/material/Skeleton';
import { TimePicker } from '@mui/x-date-pickers';
import ImageUploadForm from './components/ImageUploadForm';
import { uploadImage } from 'services/firebase';
import { EditFieldsButton, Sidebar, UpdateTableFormHeader } from './InventoryTable';
import { useDispatch } from 'react-redux';
import { newNotification } from 'actions/uiActions';

const ProductsLoading = () => {
  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <Skeleton width={'50%'} height={20} />
      </TableCell>

      <TableCell component="th" scope="row">
        <Skeleton width={'50%'} height={20} />
      </TableCell>
      <TableCell>
        <Skeleton width={'50%'} height={20} />
      </TableCell>
      <TableCell align="right">
        <Skeleton width={'30%'} height={20} />
      </TableCell>
      <TableCell align="right">
        <Skeleton width={'30%'} height={20} />
      </TableCell>
      <TableCell align="right">
        <Skeleton width={'30%'} height={20} />
      </TableCell>
      <TableCell align="right">
        <Skeleton width={'30%'} height={20} />
      </TableCell>
      <TableCell align="right">
        <Skeleton width={'30%'} height={20} />
      </TableCell>
      <TableCell align="right">
        <Skeleton width={'30%'} height={20} />
      </TableCell>
    </TableRow>
  );
};

const headCells = [
  {
    id: 'title',
    numeric: false,
    disablePadding: true,
    label: 'Title'
  },
  {
    id: 'priority',
    numeric: true,
    disablePadding: true,
    label: 'Priority'
  },
  {
    id: 'subtitle',
    numeric: true,
    disablePadding: false,
    label: 'Subtitle'
  },
  {
    id: 'image',
    numeric: true,
    disablePadding: false,
    label: 'Image'
  },
  {
    id: 'offer',
    numeric: true,
    disablePadding: false,
    label: 'Offer'
  },
  {
    id: 'store',
    numeric: true,
    disablePadding: false,
    label: 'Store'
  },
  {
    id: 'display-from',
    numeric: true,
    disablePadding: false,
    label: 'Display From'
  },
  {
    id: 'display-until',
    numeric: true,
    disablePadding: false,
    label: 'Display Until'
  }
];

function EnhancedTableHead(props) {
  return (
    <TableHead>
      <TableRow>
        <TableCell align="left"></TableCell>
        {headCells.map(headCell => (
          <TableCell key={headCell.id} align={'left'}>
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const EnhancedTableToolbar = props => {
  return (
    <>
      <Grid
        container
        justify="space-between"
        style={{ padding: '25px 15px 0px 15px' }}>
        <Grid item>
          <h3 style={{ fontSize: 20, fontFamily: 'Arial' }}>Special Offers</h3>
        </Grid>
        <Grid item>
          <button
            onClick={props.onAddItem}
            style={{
              border: '1px solid #0583FF',
              height: 37.5,
              borderRadius: 18.75,
              alignItems: 'center',
              paddingLeft: 12,
              paddingRight: 12,
              backgroundColor: 'white',
              cursor: 'pointer'
            }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#0583FF'
              }}>
              <div>
                <Add />
              </div>
              <div>
                <span style={{ fontWeight: '600' }}>Add Offer</span>
              </div>
            </div>
          </button>
        </Grid>
      </Grid>
    </>
  );
};

const UpdateItem = ({
  onClose,
  products,
  edit,
  refresh,
  stores,
  cellId,
  specialOffer
}) => {
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [offer, setOffer] = useState('');
  const [imageURL, setImageURL] = useState('');
  const [uploadUrl, setUploadUrl] = useState('');
  const [store, setStore] = useState();
  const [displayFrom, setDisplayFrom] = useState();
  const [displayTo, setDisplayTo] = useState();
  const [priority, setPriority] = useState();
  const dispatch = useDispatch();

  useEffect(() => {
    if (specialOffer) {
      setTitle(specialOffer.platformDisplayItemTitle);
      setSubTitle(specialOffer.platformDisplayItemSubTitle);
      setOffer(specialOffer.platformDisplayItemBody);
      setImageURL(specialOffer.platformDisplayItemImage);
      setStore(specialOffer.pointOfSale?.posName);
      setDisplayFrom(specialOffer.displayFrom);
      setDisplayTo(specialOffer.displayUntil);
      setPriority(parseInt(specialOffer.sortOrder));
    }
  }, [specialOffer]);
  const onChangeImage = async ({ blob, url }) => {
    setImageURL(url);
    const imageURLToSend = await uploadNewImage(blob);
    setUploadUrl(imageURLToSend);
  };

  const uploadNewImage = async blob => {
    return new Promise((resolve, reject) => {
      const uploadImageTask = uploadImage(blob.name, blob);
      uploadImageTask.on(
        'state_changed',
        () => {},
        err => {
          reject(err);
          dispatch(
            newNotification({
              variant: 'failure',
              message: 'Image upload failed: ' + err
            })
          );
        },
        async snapshot => {
          const downloadURL = await uploadImageTask.snapshot.ref.getDownloadURL();
          resolve(downloadURL);
          dispatch(
            newNotification({
              variant: 'success',
              message: 'Image uploaded'
            })
          );
        }
      );
    });
  };

  const submit = async () => {
    try {
      const specialOfferBody = {
        id: specialOffer?.id,
        platformDisplayItemTitle: title,
        platformDisplayItemSubTitle: subTitle,
        platformDisplayItemBody: offer,
        platformDisplayItemImage: uploadUrl || imageURL,
        platformItem: null,
        displayFrom: displayFrom,
        displayUntil: displayTo,
        sortOrder: parseInt(priority),
        active: specialOffer?.active
      };

      await updateSpecialOffer(specialOfferBody);
      refresh(cellId);
      dispatch(
        newNotification({
          variant: 'success',
          message: 'Offer updated'
        })
      );
    } catch (err) {
      console.log(err);
      dispatch(
        newNotification({
          variant: 'success',
          message: 'Offer update failed: ' + err
        })
      );
    }
  };

  const handleDisplayFromChange = value => {
    setDisplayFrom(value);
  };

  const handleDisplayToChange = value => {
    setDisplayTo(value);
  };
  return (
    <Sidebar
      title={edit ? 'Edit Item' : 'Add Item'}
      onClose={onClose}
      onDone={submit}>
      <div style={{ height: '81%', overflowY: 'auto' }}>
        <Grid container spacing={1} style={{ padding: 20 }}>
          <Grid item xs={12}>
            <ImageUploadForm
              imageUrl={imageURL}
              onChangeImage={onChangeImage}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth variant="standard">
              <Input
                fullWidth
                value={title}
                placeholder="Title"
                onChange={evt => {
                  setTitle(evt.target.value);
                }}
              />
              <FormHelperText id="standard-weight-helper-text">
                Title
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth variant="standard">
              <TextField
                placeholder="Subtitle"
                fullWidth
                value={subTitle}
                onChange={evt => {
                  setSubTitle(evt.target.value);
                }}
              />
              <FormHelperText id="standard-weight-helper-text">
                Subtitle
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth variant="standard">
              <Input
                placeholder="Offer"
                inputProps={{ 'aria-label': 'weight' }}
                fullWidth
                value={offer}
                onChange={evt => {
                  setOffer(evt.target.value);
                }}
              />
              <FormHelperText id="standard-weight-helper-text">
                Offer
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth variant="standard">
              <Input
                value={store}
                placeholder="Store"
                onChange={evt => {
                  setStore(evt.target.value);
                }}
                inputProps={{ 'aria-label': 'weight' }}
                fullWidth
              />
              <FormHelperText id="standard-weight-helper-text">
                Store
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TimePicker
              label="From"
              value={displayFrom}
              fullWidth
              onChange={handleDisplayFromChange}
              renderInput={params => <TextField {...params} />}
            />
          </Grid>
          <Grid item xs={12}>
            <TimePicker
              label="Until"
              value={displayTo}
              fullWidth
              onChange={handleDisplayToChange}
              renderInput={params => <TextField {...params} />}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth variant="standard">
              <Input
                value={priority}
                placeholder="Priority"
                onChange={evt => {
                  parseInt(setPriority(evt.target.value));
                }}
                type="number"
                inputProps={{ 'aria-label': 'weight' }}
                fullWidth
              />
              <FormHelperText id="standard-weight-helper-text">
                Priority
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </div>
    </Sidebar>
  );
};

const AddItem = ({ onClose, stores, refresh, cellId }) => {
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [offer, setOffer] = useState('');
  const [imageURL, setImageURL] = useState('');
  const [store, setStore] = useState();
  const [displayFrom, setDisplayFrom] = useState();
  const [displayTo, setDisplayTo] = useState();
  const [submitting, setSubmitting] = useState(false);
  const [blob, setBlob] = useState();
  const [priority, setPriority] = useState();

  const onSubmit = async () => {
    try {
      setSubmitting(true);
      const uploadImageTask = uploadImage(blob.name, blob);
      uploadImageTask.on(
        'state_changed',
        () => {},
        onErrorUpload,
        async snapshot => {
          try {
            const downloadURL = await uploadImageTask.snapshot.ref.getDownloadURL();
            const posId = stores[store].id;
            const specialOfferBody = {
              platformDisplayId: cellId,
              platformDisplayItemTypeId: 3,
              platformDisplayItemTitle: title,
              platformDisplayItemSubTitle: subTitle,
              platformDisplayItemBody: offer,
              platformDisplayItemImage: downloadURL,
              platformItemId: null,
              displayFrom: displayFrom.unix(),
              displayUntil: displayTo.unix(),
              posId: posId,
              sortOrder: priority
            };
            await addSpecialOffer(specialOfferBody);
            refresh(cellId);
            setSubmitting(false);
          } catch (err) {
            console.log(err);
          }
        }
      );
    } catch (err) {
      console.log(err);
      setSubmitting(false);
    }
  };

  const onErrorUpload = async () => {
    alert('FAILED');
  };

  const onChangeImage = ({ blob, url }) => {
    setBlob(blob);
    setImageURL(url);
  };

  const handleChangeStore = evt => {
    setStore(evt.target.value);
  };

  const handleDisplayFromChange = value => {
    setDisplayFrom(value);
  };

  const handleDisplayToChange = value => {
    setDisplayTo(value);
  };

  const storesNames = Object.keys(stores);
  const doneEnabled =
    title &&
    subTitle &&
    offer &&
    imageURL &&
    displayFrom &&
    displayTo &&
    store &&
    blob;

  return (
    <Paper
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        height: '100%',
        width: 350,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
      <UpdateTableFormHeader title={'Add Offer'} onClose={onClose} />
      <div style={{ position: 'relative', paddingBottom: 20, height: '100%' }}>
        <div style={{ height: '78%', overflowY: 'auto' }}>
          <div style={{ paddingLeft: 20, paddingRight: 20 }}>
            <ImageUploadForm
              imageUrl={imageURL}
              onChangeImage={onChangeImage}
            />
          </div>
          <TextField
            placeholder="Title"
            fullWidth
            style={{ padding: 20, marginTop: 30 }}
            value={title}
            onChange={evt => {
              setTitle(evt.target.value);
            }}
          />
          <TextField
            placeholder="Subtitle"
            fullWidth
            style={{ padding: 20 }}
            value={subTitle}
            onChange={evt => {
              setSubTitle(evt.target.value);
            }}
          />
          <TextField
            placeholder="Offer"
            fullWidth
            style={{ padding: 20 }}
            value={offer}
            onChange={evt => {
              setOffer(evt.target.value);
            }}
          />
          <div style={{ padding: 20 }}>
            <FormControl variant="standard" fullWidth>
              <InputLabel id="store-label">Store</InputLabel>
              <Select
                labelId="store-label"
                id="store-select"
                value={store}
                onChange={handleChangeStore}
                label="Store">
                {storesNames.map(store => {
                  return <MenuItem value={store}>{store}</MenuItem>;
                })}
              </Select>
            </FormControl>
          </div>
          <div style={{ padding: 20 }}>
            <TimePicker
              label="Display From"
              value={displayFrom}
              fullWidth
              onChange={handleDisplayFromChange}
              renderInput={params => <TextField {...params} />}
            />
          </div>
          <div style={{ padding: 20 }}>
            <TimePicker
              label="Until"
              value={displayTo}
              fullWidth
              onChange={handleDisplayToChange}
              renderInput={params => <TextField {...params} />}
            />
          </div>
          <div style={{ padding: 20 }}>
            <FormControl fullWidth variant="standard">
              <Input
                value={priority}
                placeholder="Priority"
                onChange={evt => {
                  parseInt(setPriority(evt.target.value));
                }}
                type="number"
                inputProps={{ 'aria-label': 'weight' }}
                fullWidth
              />
              <FormHelperText id="standard-weight-helper-text">
                Priority
              </FormHelperText>
            </FormControl>
          </div>
        </div>
        <div
          style={{
            bottom: 0,
            padding: 24,
            borderTop: '1px solid #efefef',
            backgroundColor: 'white',
            position: 'absolute',
            width: '100%',
            display: 'flex',
            flexDirection: 'row'
          }}>
          <div style={{ flex: 1 }}>
            <button
              style={{
                padding: 20,
                width: '100%',
                backgroundColor: doneEnabled || submitting ? '#0583FF' : 'gray',
                border: 'none',
                fontWeight: 'normal',
                borderRadius: 52,
                color: 'white',
                fontSize: 16,
                fontFamily: 'Arial',
                cursor: 'pointer'
              }}
              onClick={onSubmit}
              disabled={!doneEnabled || submitting}>
              {submitting ? <CircularProgress size="small" /> : 'Done'}
            </button>
          </div>
        </div>
      </div>
    </Paper>
  );
};

const DeleteFieldsButton = ({ numbToEdit, onClick }) => {
  return (
    <div
      style={{
        position: 'absolute',
        backgroundColor: 'red',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        bottom: 50,
        left: '20%',
        width: 276,
        padding: '16px 24px',
        boxShadow: '0px 8px 16px rgba(20, 20, 43, 0.24)',
        height: 56,
        borderRadius: 30,
        color: 'white',
        fontFamily: 'Arial',
        fontWeight: '600',
        cursor: 'pointer'
      }}
      onClick={onClick}>
      <Grid container justify="space-between" style={{ marginTop: 3 }}>
        <Grid item>
          <span>{numbToEdit} offers selected</span>
        </Grid>
        <Grid item>
          <span
            style={{
              color: 'white',
              fontFamily: 'Arial',
              textDecoration: 'underline'
            }}>
            Delete
          </span>
        </Grid>
      </Grid>
    </div>
  );
};

export default function EnhancedTable({ cellId }) {
  const [selected, setSelected] = React.useState([]);
  const [specialOffers, setSpecialOffers] = useState([]);
  const [fetchingProducts, setFetchingProducts] = useState(true);
  const [pointOfSales, setPointOfSales] = useState({});
  const [showAddItem, setShowAddItem] = useState(false);
  const [showEditItems, setShowEditItems] = useState(false);

  useEffect(() => {
    refreshProducts(cellId);
  }, [cellId]);

  const refreshProducts = async cellId => {
    try {
      setShowAddItem(false);
      setFetchingProducts(true);
      const specialOffers = await getSpecialOffers(cellId);
      setSpecialOffers(specialOffers);
      const stores = await getPointOfSales(cellId);
      const storesKeyVal = arrayToKeys('posName', stores);
      setPointOfSales(storesKeyVal.objectWithKeys);
      setFetchingProducts(false);
    } catch (err) {
      setFetchingProducts(false);
    }
  };

  const handleClick = (event, offer) => {
    const selectedIndex = selected.findIndex(
      of => of.platformDisplayItemTitle === offer.platformDisplayItemTitle
    );
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, offer);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const onDeleteItems = async () => {
    try {
      const deleteReqs = selected.map(specialOffer => {
        return deleteSpecialOffer(specialOffer.id);
      });
      Promise.all(deleteReqs)
        .then(res => {
          setSelected([]);
          refreshProducts();
        })
        .catch(err => {
          console.log(err);
        });
    } catch (err) {
      console.log(err);
    }
  };

  const isSelected = name => {
    return (
      selected.findIndex(offer => offer.platformDisplayItemTitle === name) !==
      -1
    );
  };

  return (
    <div sx={{ width: '100%' }}>
      <Paper
        sx={{ mb: 2 }}
        style={{ position: 'relative', top: 0, left: 0, width: '100%' }}>
        <EnhancedTableToolbar
          onAddItem={() => {
            setShowAddItem(true);
          }}
        />
        <TableContainer
          style={{
            height: Math.round(window.innerHeight * 0.75),
            overflow: 'auto'
          }}>
          <Table sx={{ minWidth: 750 }} size="small">
            <EnhancedTableHead />
            <TableBody style={{ height: 500, overflowY: 'auto' }}>
              {fetchingProducts && (
                <>
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                  <ProductsLoading />
                </>
              )}
              {!fetchingProducts &&
                specialOffers
                  .sort((a, b) => a?.sortOrder - b?.sortOrder)
                  .map((row, index) => {
                    const isItemSelected = isSelected(
                      row.platformDisplayItemTitle
                    );
                    const labelId = `enhanced-table-checkbox-${index}`;

                    const fromTime = new Date(
                      row.displayFrom
                    ).toLocaleTimeString();
                    const untilTime = new Date(
                      row.displayUntil
                    ).toLocaleTimeString();

                    return (
                      <TableRow
                        hover
                        onClick={event => handleClick(event, row)}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={`${row.productName}_${index}`}
                        selected={isItemSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isItemSelected}
                            inputProps={{
                              'aria-labelledby': labelId
                            }}
                          />
                        </TableCell>
                        <TableCell
                          component="th"
                          id={labelId}
                          scope="row"
                          padding="none">
                          {row.platformDisplayItemTitle}
                        </TableCell>
                        <TableCell>{row.sortOrder}</TableCell>
                        <TableCell>{row.platformDisplayItemSubTitle}</TableCell>
                        <TableCell>
                          <img
                            alt="Special offer"
                            src={row.platformDisplayItemImage}
                            style={{ height: 80, width: 150 }}></img>
                        </TableCell>
                        <TableCell>{row.platformDisplayItemBody}</TableCell>
                        <TableCell component="th" scope="row">
                          {row.pointOfSale.posName}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {fromTime}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {untilTime}
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>
        {selected.length > 0 && !showEditItems && (
          <DeleteFieldsButton
            numbToEdit={selected.length}
            onClick={onDeleteItems}
          />
        )}

        {selected.length === 1 && !showEditItems && (
          <EditFieldsButton
            numbToEdit={selected.length}
            onClick={() => {
              setShowEditItems(true);
            }}
          />
        )}
        {showAddItem && (
          <AddItem
            stores={pointOfSales}
            refresh={refreshProducts}
            onClose={() => {
              setShowAddItem(false);
            }}
            onSubmit={() => {}}
            cellId={cellId}
          />
        )}
        {showEditItems && (
          <UpdateItem
            edit
            stores={pointOfSales}
            products={selected}
            onClose={() => {
              setShowEditItems(false);
              setSelected([]);
            }}
            refresh={refreshProducts}
            cellId={cellId}
            specialOffer={selected[0]}
          />
        )}
      </Paper>
    </div>
  );
}
